// The token DJI Pilot's api and ws modules carry. Pilot sends it as
// x-auth-token from native code, which never sees this page's cookie, and the
// gateway resolves it as a Kratos session (iam's session bridge). The page
// signs in through the browser flow and so holds no token of its own: it
// trades its cookie for one at iam (POST identity/device/refresh-session, the
// mint skyclient boxes rotate with). That is a second Kratos session of the
// same identity, so the page keeps track of it: each start mints a fresh one,
// and Log Out revokes it (autrik_codex T90 §12).
//
// A start does not revoke the previous start's session at once. Pilot's
// modules keep using the token they were given until the page loads them
// again, and a 401 on their requests makes Pilot leave the cloud webview
// (seen on the RC 2026-10-21 after every refresh). The old sessions are queued
// as retired and revoked once the modules carry the new token.

import { ApiError } from './api';
import type { Config } from './config';

const KEY = 'pilotSession';

export interface PilotSession {
  token: string;
  sessionId: string;
}

interface Stored extends PilotSession {
  retired: PilotSession[];
}

/** The slice of localStorage this needs; absent in a private window. */
export type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function browserStore(): Store | undefined {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
}

const isSession = (s: Partial<PilotSession> | null | undefined): s is PilotSession => !!s?.token && !!s.sessionId;

function stored(store: Store | undefined): Stored | undefined {
  try {
    const raw = JSON.parse(store?.getItem(KEY) ?? 'null') as (Partial<PilotSession> & { retired?: unknown }) | null;
    const list = Array.isArray(raw?.retired) ? (raw.retired as Partial<PilotSession>[]) : [];
    if (!isSession(raw)) return undefined;
    const retired = list.filter(isSession).map(({ token, sessionId }) => ({ token, sessionId }));
    return { token: raw.token, sessionId: raw.sessionId, retired };
  } catch {
    return undefined;
  }
}

function remember(store: Store | undefined, s: Stored | undefined): void {
  try {
    if (s) store?.setItem(KEY, JSON.stringify(s));
    else store?.removeItem(KEY);
  } catch {
    /* private mode: the session ends at its expiry instead */
  }
}

async function post(fetchFn: typeof fetch, name: string, url: string, init: RequestInit): Promise<unknown> {
  const res = await fetchFn(url, { method: 'POST', ...init, headers: { Accept: 'application/json', ...(init.headers ?? {}) } });
  const text = await res.text();
  if (!res.ok) throw new ApiError(name, res.status, text);
  return text ? JSON.parse(text) : null;
}

/** A 401 on a revoke is a session that has already ended: nothing to report. */
const gone = (e: unknown) => e instanceof ApiError && e.status === 401;

/** Revoke one minted session, proving it with that session's own token. */
async function revoke(cfg: Config, s: PilotSession, fetchFn: typeof fetch): Promise<void> {
  await post(fetchFn, 'revoke-previous-session', `${cfg.backend}/identity/device/revoke-previous-session`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s.token}` },
    body: JSON.stringify({ session_id: s.sessionId }),
  });
}

/**
 * A fresh token for Pilot's modules, minted from the cookie session. The one
 * minted on the previous start joins the retired queue: revokeRetired ends it
 * once the modules carry the new token.
 */
export async function pilotToken(cfg: Config, fetchFn: typeof fetch = fetch, store: Store | undefined = browserStore()): Promise<string> {
  const r = (await post(fetchFn, 'refresh-session', `${cfg.backend}/identity/device/refresh-session`, {
    credentials: 'include',
  })) as { token?: string; session_id?: string } | null;
  if (!r?.token || !r.session_id) throw new ApiError('refresh-session', 200, 'no token');
  const before = stored(store);
  const retired = before ? [...before.retired, { token: before.token, sessionId: before.sessionId }] : [];
  remember(store, { token: r.token, sessionId: r.session_id, retired: retired.filter((s) => s.sessionId !== r.session_id) });
  return r.token;
}

/**
 * Revoke the retired sessions. Call it only once Pilot's api and ws modules
 * have been loaded with the current token. A session that fails to revoke
 * (other than already gone) stays queued for the next start.
 */
export async function revokeRetired(
  cfg: Config,
  onError: (e: unknown) => void = () => {},
  fetchFn: typeof fetch = fetch,
  store: Store | undefined = browserStore(),
): Promise<void> {
  const s = stored(store);
  if (!s?.retired.length) return;
  const kept: PilotSession[] = [];
  for (const old of s.retired) {
    try {
      await revoke(cfg, old, fetchFn);
    } catch (e) {
      if (gone(e)) continue;
      onError(e);
      kept.push(old);
    }
  }
  // Re-read: a newer start may have queued more meanwhile. Drop only what this
  // call ended.
  const ended = new Set(s.retired.filter((o) => !kept.includes(o)).map((o) => o.sessionId));
  const now = stored(store);
  if (now) remember(store, { ...now, retired: now.retired.filter((r) => !ended.has(r.sessionId)) });
}

/** Log Out: revoke the minted sessions too, so Pilot's modules lose them with the login. */
export async function endPilotToken(cfg: Config, fetchFn: typeof fetch = fetch, store: Store | undefined = browserStore()): Promise<void> {
  const s = stored(store);
  remember(store, undefined);
  if (!s) return;
  let failure: unknown;
  for (const old of [{ token: s.token, sessionId: s.sessionId }, ...s.retired]) {
    try {
      await revoke(cfg, old, fetchFn);
    } catch (e) {
      if (!gone(e)) failure ??= e;
    }
  }
  if (failure) throw failure;
}
